import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { api, API } from '@/lib/api';
import { useAuth } from '@/context/AuthContext';
import { FileText, Download, ShieldAlert, FilePlus, Loader2, AlertOctagon, ShieldCheck, CreditCard, Lock } from 'lucide-react';
import { toast } from 'sonner';

const statusPill = { draft: 'pill-medium', final: 'pill-low', published: 'pill-brand' };

const ForensicReports = () => {
  const { user, refresh } = useAuth();
  const [reports, setReports] = useState([]);
  const [active, setActive] = useState(null);
  const [loading, setLoading] = useState(true);
  const [requesting, setRequesting] = useState(false);
  const [showConsent, setShowConsent] = useState(false);
  const [consentChecked, setConsentChecked] = useState(false);
  const [upgrading, setUpgrading] = useState(false);

  const fmtUSD = (cents) => `$${((cents || 0) / 100).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

  // Certified-report credit lockout. When the customer's credit balance is below the
  // required certified-report cost AND the account is not paid in full, all
  // forensic-report generation and PDF downloads are locked behind a secondary payment.
  const creditsAvailable = Number(user?.credits ?? 0);
  const certifiedCost = Number(user?.certified_report_cost ?? 2000);
  const canGenerateCertified = user?.role === 'admin' || (user?.can_generate_certified ?? (creditsAvailable >= certifiedCost));
  const certifiedLocked = !canGenerateCertified;

  const isConfirmed = active?.classification === 'Confirmed Findings';
  const paymentRequired = isConfirmed && (user?.outstanding_balance_cents || 0) > 0 && !user?.confirmed_report_access;
  const pdfLocked = paymentRequired || certifiedLocked;

  useEffect(() => {
    api.get('/forensic-reports').then((r) => {
      setReports(r.data);
      if (r.data[0]) setActive(r.data[0]);
      setLoading(false);
    });
  }, []);

  const requestReport = async () => {
    if (!consentChecked) {
      toast.error('Please confirm authorization to proceed.');
      return;
    }
    setRequesting(true);
    try {
      const res = await api.post('/forensic-reports/request', {
        investigation_type: 'Cybersecurity Activity Review',
      });
      const newReport = res.data;
      setReports((prev) => [newReport, ...prev]);
      setActive(newReport);
      setShowConsent(false);
      setConsentChecked(false);
      toast.success('Forensic report requested. A Web PI analyst will review shortly.');
    } catch (e) {
      const detail = e?.response?.data?.detail;
      const msg = (detail && typeof detail === 'object' && detail.message) || (typeof detail === 'string' ? detail : null) || 'Could not request report.';
      toast.error(msg);
      if (e?.response?.status === 402) {
        setShowConsent(false);
      }
    } finally {
      setRequesting(false);
    }
  };

  return (
    <div className="space-y-8">
      <header className="flex flex-col md:flex-row md:items-end justify-between gap-4">
        <div>
          <div className="label-tag mb-2">// Web PI Forensic Reports</div>
          <h1 className="text-4xl md:text-5xl font-light tracking-tight">Investigation reports</h1>
          <p className="text-[color:var(--text-secondary)] mt-2 max-w-3xl text-sm md:text-base">
            Case <span className="font-mono text-[color:var(--brand)]">{user?.case_number || '#PENDING'}</span> — Web PI forensic deliverables compiled from your login feed, network scans, and analyst review.
          </p>
        </div>
        <button
          type="button"
          onClick={() => certifiedLocked ? toast.error('You have used all your credits. A secondary payment is required to continue and generate a certified forensic report.') : setShowConsent(true)}
          disabled={certifiedLocked}
          className="btn-primary shrink-0 disabled:opacity-40 disabled:cursor-not-allowed"
          data-testid="request-forensic-report-btn"
          title={certifiedLocked ? 'Locked — secondary payment required' : 'Request a new forensic report'}
        >
          {certifiedLocked ? <><Lock size={14} /> Locked — Pay to Continue</> : <><FilePlus size={14} /> Request Forensic Report</>}
        </button>
      </header>

      {certifiedLocked && (
        <div
          className="panel p-5 sm:p-6 border-[color:var(--status-critical)] bg-[rgba(239,68,68,0.08)]"
          data-testid="certified-report-locked-banner"
        >
          <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4">
            <div className="flex items-start gap-3">
              <Lock className="text-[color:var(--status-critical)] mt-0.5 shrink-0" size={20} />
              <div>
                <div className="label-tag mb-1 text-[color:var(--status-critical)]">// Account Locked · Credits Exhausted</div>
                <h2 className="text-lg sm:text-xl font-light tracking-tight">You have used all your credits.</h2>
                <p className="text-sm text-[color:var(--text-secondary)] leading-relaxed mt-2 max-w-2xl">
                  A secondary payment is required to continue and generate a certified forensic report. New report requests and PDF downloads are locked until your account is brought current.
                </p>
                <div className="font-mono text-[10px] uppercase tracking-widest text-[color:var(--text-muted)] mt-3 flex flex-wrap gap-x-4 gap-y-1">
                  <span data-testid="certified-locked-credits">
                    Credits Available: <span className="text-[color:var(--status-critical)] text-base normal-case font-sans font-semibold">{creditsAvailable.toLocaleString()}</span>
                  </span>
                  <span data-testid="certified-locked-required">
                    Required: <span className="text-[color:var(--text-primary)] text-base normal-case font-sans font-semibold">{certifiedCost.toLocaleString()}</span>
                  </span>
                </div>
              </div>
            </div>
            <div className="font-mono text-[10px] uppercase tracking-widest text-[color:var(--status-critical)] shrink-0 sm:text-right" data-testid="certified-locked-status">
              Status · Secondary Payment Required
            </div>
          </div>
        </div>
      )}

      {showConsent && (
        <div className="panel p-6 sm:p-8 border-[color:var(--brand)] bg-[rgba(0,195,212,0.06)]" data-testid="forensic-consent-panel">
          <div className="label-tag text-[color:var(--brand)] mb-2">// Authorization Required</div>
          <h2 className="text-xl font-light tracking-tight mb-3">Request Web PI Forensic Review</h2>
          <p className="text-sm text-[color:var(--text-secondary)] leading-relaxed mb-4 max-w-3xl">
            Supreme Web Security will compile a forensic investigation report covering login activity, authentication events, security alerts, and related platform telemetry for review.
          </p>
          <label className="flex items-start gap-3 cursor-pointer mb-5" data-testid="forensic-consent-label">
            <input
              type="checkbox"
              className="mt-1 h-4 w-4 accent-[color:var(--brand)]"
              checked={consentChecked}
              onChange={(e) => setConsentChecked(e.target.checked)}
              data-testid="forensic-consent-checkbox"
            />
            <span className="text-sm text-[color:var(--text-secondary)] leading-relaxed">
              I confirm that I own or have authorization to scan this network and request Supreme Web Security to perform a security review.
            </span>
          </label>
          <div className="flex flex-wrap gap-3">
            <button
              type="button"
              onClick={requestReport}
              disabled={!consentChecked || requesting}
              className="btn-primary disabled:opacity-40 disabled:cursor-not-allowed"
              data-testid="forensic-consent-submit"
            >
              {requesting ? <><Loader2 size={14} className="animate-spin" /> Generating…</> : <><FileText size={14} /> Submit Request</>}
            </button>
            <button
              type="button"
              onClick={() => { setShowConsent(false); setConsentChecked(false); }}
              className="btn-secondary"
              data-testid="forensic-consent-cancel"
            >
              Cancel
            </button>
          </div>
        </div>
      )}

      {reports.length === 0 && !loading && (
        <div className="panel p-6 sm:p-8 border-[color:var(--status-medium)] bg-[rgba(255,179,2,0.06)] flex items-start gap-4" data-testid="no-reports-banner">
          <ShieldAlert className="text-[color:var(--status-medium)] mt-1" size={20} />
          <div className="flex-1">
            <div className="label-tag text-[color:var(--status-medium)] mb-1">// No reports yet</div>
            <p className="text-sm text-[color:var(--text-secondary)] leading-relaxed">
              Your case is open. A Supreme Web Security investigator will compile your first forensic report shortly. You&apos;ll be able to download a PDF and review the timeline, observed indicators, and recommendations here.
            </p>
          </div>
        </div>
      )}

      {reports.length > 0 && (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-px bg-[color:var(--border)]" data-testid="forensic-reports-grid">
          <aside className="bg-[color:var(--bg-base)] p-4 sm:p-5 lg:col-span-1">
            <div className="label-tag mb-3">// Available Reports</div>
            <ul className="space-y-2">
              {reports.map((r) => (
                <li key={r.report_id}>
                  <button
                    type="button"
                    onClick={() => setActive(r)}
                    className={`w-full text-left panel-soft p-3 hover:border-[color:var(--brand)] transition-colors ${active?.report_id === r.report_id ? 'border-[color:var(--brand)]' : ''}`}
                    data-testid="forensic-report-item"
                  >
                    <div className="font-mono text-[10px] uppercase tracking-widest text-[color:var(--brand)]">{r.case_number}</div>
                    <div className="text-sm mt-1">{r.investigation_type}</div>
                    <div className="font-mono text-[10px] uppercase tracking-widest text-[color:var(--text-muted)] mt-1">
                      <span className={`pill ${statusPill[r.status] || 'pill-muted'}`}>{r.status}</span>
                      <span className="ml-2">{new Date(r.created_at).toLocaleDateString()}</span>
                    </div>
                  </button>
                </li>
              ))}
            </ul>
          </aside>

          <article className="bg-[color:var(--bg-base)] p-5 sm:p-7 lg:col-span-2 space-y-6" data-testid="forensic-report-detail">
            {active ? (
              <>
                <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                  <div>
                    <div className="font-mono text-[10px] uppercase tracking-widest text-[color:var(--brand)]">{active.case_number}</div>
                    <h2 className="text-2xl sm:text-3xl font-light tracking-tight">{active.investigation_type}</h2>
                    <div className="font-mono text-[10px] uppercase tracking-widest text-[color:var(--text-muted)] mt-1">
                      Range {active.requested_range_start} – {active.requested_range_end} · Prepared by {active.prepared_by}
                    </div>
                    <div className="flex flex-wrap gap-2 mt-3" data-testid="forensic-report-classification-row">
                      <span className={`pill ${active.classification === 'Confirmed Findings' ? 'pill-critical' : 'pill-medium'}`} data-testid="forensic-classification-pill">
                        {active.classification || 'Preliminary Findings'}
                      </span>
                      <span className={`pill ${active.final_determination === 'Unauthorized Access Confirmed' ? 'pill-critical' : 'pill-muted'}`} data-testid="forensic-final-determination-pill">
                        {active.final_determination || 'Pending Review'}
                      </span>
                    </div>
                  </div>
                  <div className="flex flex-col gap-2 shrink-0">
                    {pdfLocked ? (
                      <button
                        type="button"
                        disabled
                        className="btn-primary !py-2 !text-[11px] disabled:opacity-40 disabled:cursor-not-allowed"
                        data-testid="forensic-report-pdf-locked"
                        title="Must complete second payment to download"
                      >
                        <Lock size={12} /> Download PDF (Locked)
                      </button>
                    ) : (
                      <a
                        href={`${API}/forensic-reports/${active.report_id}/pdf`}
                        target="_blank"
                        rel="noreferrer"
                        className="btn-primary !py-2 !text-[11px]"
                        data-testid="forensic-report-pdf"
                      >
                        <Download size={12} /> Download PDF
                      </a>
                    )}
                    {isConfirmed && (
                      user?.upgrade_status === 'approved' ? (
                        <div className="font-mono text-[10px] uppercase tracking-widest text-[color:var(--status-low)] flex items-center gap-1.5" data-testid="forensic-upgrade-approved">
                          <ShieldCheck size={11} /> Web PI Package · Approved
                        </div>
                      ) : user?.upgrade_status === 'pending_admin' ? (
                        <div className="font-mono text-[10px] uppercase tracking-widest text-[color:var(--status-medium)] flex items-center gap-1.5" data-testid="forensic-upgrade-pending">
                          <ShieldCheck size={11} /> Upgrade · Awaiting admin
                        </div>
                      ) : (
                        <button
                          type="button"
                          onClick={async () => {
                            setUpgrading(true);
                            try {
                              await api.post(`/forensic-reports/${active.report_id}/request-upgrade`, { package: 'web_pi' });
                              await refresh();
                              toast.success('Upgrade requested. Must complete second payment to download.');
                            } catch (e) {
                              toast.error(e?.response?.data?.detail || 'Could not request upgrade.');
                            } finally { setUpgrading(false); }
                          }}
                          disabled={upgrading}
                          className="btn-secondary !py-2 !text-[11px] disabled:opacity-40"
                          data-testid="forensic-upgrade-btn"
                        >
                          <CreditCard size={12} /> {upgrading ? 'Requesting…' : 'Upgrade Package'}
                        </button>
                      )
                    )}
                    {!isConfirmed && (
                      active.customer_requested_confirmation ? (
                        <div className="font-mono text-[10px] uppercase tracking-widest text-[color:var(--status-medium)] flex items-center gap-1.5" data-testid="forensic-confirmation-pending">
                          <ShieldCheck size={11} /> Confirmation pending admin
                        </div>
                      ) : (
                        <button
                          type="button"
                          onClick={async () => {
                            try {
                              const res = await api.post(`/forensic-reports/${active.report_id}/request-confirmation`);
                              setActive({ ...active, customer_requested_confirmation: true });
                              setReports((prev) => prev.map((p) => p.report_id === active.report_id ? { ...p, customer_requested_confirmation: true } : p));
                              toast.success('Confirmation requested. An investigator will review and publish a Confirmed Findings update.');
                            } catch (e) {
                              toast.error(e?.response?.data?.detail || 'Could not request confirmation.');
                            }
                          }}
                          className="btn-secondary !py-2 !text-[11px]"
                          data-testid="forensic-request-confirmation-btn"
                        >
                          <ShieldCheck size={12} /> Request Confirmed Findings
                        </button>
                      )
                    )}
                  </div>
                </div>

                {/* Second-payment gating banner — Confirmed Findings + outstanding balance + no access */}
                {paymentRequired && (
                  <div
                    className="panel p-4 sm:p-5 border-[color:var(--status-critical)] bg-[rgba(239,68,68,0.06)]"
                    data-testid="forensic-payment-required-banner"
                  >
                    <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                      <div>
                        <div className="label-tag mb-1 text-[color:var(--status-critical)] flex items-center gap-1.5">
                          <Lock size={11} /> // Second Payment Required
                        </div>
                        <div className="text-sm leading-relaxed">
                          Must complete second payment to download.
                        </div>
                        <div className="font-mono text-xs uppercase tracking-widest text-[color:var(--text-muted)] mt-2 flex flex-wrap gap-x-4 gap-y-1">
                          <span>Current Balance: <span className="text-[color:var(--status-critical)] text-base normal-case font-sans font-semibold" data-testid="forensic-current-balance">{fmtUSD(user?.outstanding_balance_cents)}</span></span>
                          <span>Unlock with: <span className="text-[color:var(--text-primary)] text-base normal-case font-sans font-semibold" data-testid="forensic-payment-required">{fmtUSD(user?.payment_required_cents)}</span></span>
                        </div>
                      </div>
                      <div className="font-mono text-[10px] uppercase tracking-widest text-[color:var(--text-muted)] shrink-0" data-testid="forensic-payment-status">
                        {user?.upgrade_status === 'pending_admin'
                          ? 'Status · Awaiting admin approval'
                          : 'Status · Action required'}
                      </div>
                    </div>
                  </div>
                )}

                {(active.evidence_of_compromise || active.findings_summary?.evidence_of_compromise) && (
                  <section data-testid="forensic-evidence-of-compromise">
                    <div className="label-tag mb-2 text-[color:var(--status-critical)] flex items-center gap-1.5">
                      <AlertOctagon size={12} /> // Evidence of Compromise
                    </div>
                    <div className="panel-soft p-4 text-sm whitespace-pre-wrap leading-relaxed border-l-2 border-l-[color:var(--status-critical)]">
                      {active.evidence_of_compromise || active.findings_summary?.evidence_of_compromise}
                    </div>
                  </section>
                )}

                {(active.devices_accessed_system || []).length > 0 && (
                  <section data-testid="forensic-devices-accessed">
                    <div className="label-tag mb-2 text-[color:var(--status-critical)]">// Devices That Accessed System</div>
                    <div className="overflow-x-auto">
                      <table className="w-full text-xs">
                        <thead>
                          <tr className="text-left font-mono uppercase tracking-widest text-[10px] text-[color:var(--text-muted)] border-b border-[color:var(--border)]">
                            <th className="py-2 pr-3">Device</th>
                            <th className="py-2 pr-3">IP</th>
                            <th className="py-2 pr-3">MAC</th>
                            <th className="py-2 pr-3">Type</th>
                            <th className="py-2 pr-3">Classification</th>
                          </tr>
                        </thead>
                        <tbody>
                          {active.devices_accessed_system.map((d, i) => (
                            <tr key={i} className="border-b border-[color:var(--border)]/40">
                              <td className="py-2 pr-3 text-sm">{d.name}</td>
                              <td className="py-2 pr-3 font-mono">{d.ip}</td>
                              <td className="py-2 pr-3 font-mono text-[10px]">{d.mac}</td>
                              <td className="py-2 pr-3">{d.device_type}</td>
                              <td className="py-2 pr-3">
                                <span className={`pill ${d.classification === 'Attacker Artifact' ? 'pill-critical' : 'pill-medium'}`}>{d.classification}</span>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </section>
                )}

                {(active.endpoint_activity || []).length > 0 && (
                  <section data-testid="forensic-endpoint-activity">
                    <div className="label-tag mb-2 text-[color:#3b82f6]">// Insurance Endpoint Activity — Accessed Systems</div>
                    <div className="panel-soft p-4 mb-3 text-xs">
                      <div className="font-mono uppercase tracking-widest text-[10px] text-[color:var(--text-muted)]">Endpoint</div>
                      <div className="text-sm font-semibold mt-1">{active.endpoint_activity[0].endpoint_label}</div>
                      <div className="font-mono text-[10px] text-[color:var(--text-muted)] mt-1">
                        Origin: {active.endpoint_activity[0].origin} · IP: {active.endpoint_activity[0].ip}
                      </div>
                    </div>
                    <div className="overflow-x-auto">
                      <table className="w-full text-xs">
                        <thead>
                          <tr className="text-left font-mono uppercase tracking-widest text-[10px] text-[color:var(--text-muted)] border-b border-[color:var(--border)]">
                            <th className="py-2 pr-3">Timestamp UTC</th>
                            <th className="py-2 pr-3">Endpoint</th>
                            <th className="py-2 pr-3">Target System</th>
                            <th className="py-2 pr-3">Event</th>
                          </tr>
                        </thead>
                        <tbody>
                          {active.endpoint_activity.map((e, i) => (
                            <tr key={i} className="border-b border-[color:var(--border)]/40">
                              <td className="py-2 pr-3 font-mono text-[10px]">{e.timestamp}</td>
                              <td className="py-2 pr-3 font-mono text-[11px] text-[color:#3b82f6]">{e.endpoint}</td>
                              <td className="py-2 pr-3 text-sm">{e.target}</td>
                              <td className="py-2 pr-3 text-xs">{e.event}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </section>
                )}

                {active.state_farm_check && active.state_farm_check.check_number && (
                  <section data-testid="forensic-check-evidence">
                    <div className="label-tag mb-2 text-[color:#3b82f6]">// Payment Instrument Evidence — Watermarked Thumbnail</div>
                    <div className="panel-soft p-4 grid grid-cols-1 sm:grid-cols-[260px_1fr] gap-4 items-start">
                      <div className="border border-[color:var(--border)] bg-[#f6f6f1] overflow-hidden">
                        <img
                          src={`${API}/forensic-reports/${active.report_id}/check-thumbnail.png`}
                          alt="State Farm check thumbnail (watermarked)"
                          className="w-full block"
                          data-testid="forensic-check-thumbnail"
                        />
                      </div>
                      <div className="space-y-2 text-sm">
                        <div className="grid grid-cols-2 gap-x-3 gap-y-1 text-xs font-mono">
                          <span className="text-[color:var(--text-muted)] uppercase tracking-widest">Issuer</span>
                          <span>{active.state_farm_check.issuer}</span>
                          <span className="text-[color:var(--text-muted)] uppercase tracking-widest">Check #</span>
                          <span>{active.state_farm_check.check_number}</span>
                          <span className="text-[color:var(--text-muted)] uppercase tracking-widest">Issued</span>
                          <span>{active.state_farm_check.issued_date}</span>
                          <span className="text-[color:var(--text-muted)] uppercase tracking-widest">Amount</span>
                          <span className="text-[color:var(--status-critical)] text-base font-semibold">{active.state_farm_check.amount_label}</span>
                          <span className="text-[color:var(--text-muted)] uppercase tracking-widest">Memo</span>
                          <span>{active.state_farm_check.memo}</span>
                          <span className="text-[color:var(--text-muted)] uppercase tracking-widest">Pay To</span>
                          <span>{active.state_farm_check.pay_to}</span>
                        </div>
                        {user?.upgrade_status === 'paid_in_full' && (user?.outstanding_balance_cents || 0) === 0 ? (
                          <a
                            href={`${API}/forensic-reports/${active.report_id}/check-full.png`}
                            target="_blank"
                            rel="noreferrer"
                            className="btn-primary !py-2 !text-[11px] inline-flex"
                            data-testid="forensic-check-download-full"
                          >
                            <Download size={12} /> Download Full Check Image
                          </a>
                        ) : (
                          <div className="flex flex-col gap-2">
                            <button
                              type="button"
                              disabled
                              className="btn-primary !py-2 !text-[11px] inline-flex opacity-40 cursor-not-allowed"
                              data-testid="forensic-check-download-locked"
                              title="Account must be paid in full to download"
                            >
                              <Lock size={12} /> Download Full Check (Locked)
                            </button>
                            <div className="font-mono text-[10px] uppercase tracking-widest text-[color:var(--status-critical)]" data-testid="forensic-check-paid-in-full-required">
                              · Account must be paid in full to download the check image
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  </section>
                )}

                <section>
                  <div className="label-tag mb-2">// Systems Included</div>
                  <ul className="flex flex-wrap gap-2">
                    {(active.systems_included || []).map((s) => (
                      <li key={s} className="pill pill-muted">{s}</li>
                    ))}
                  </ul>
                </section>

                <section className="grid grid-cols-2 md:grid-cols-4 gap-px bg-[color:var(--border)]">
                  {Object.entries(active.stats || {}).map(([k, v]) => (
                    <div key={k} className="bg-[color:var(--bg-base)] p-3 sm:p-4">
                      <div className="font-mono text-[9.5px] uppercase tracking-widest text-[color:var(--text-muted)]">{k.replace(/_/g, ' ')}</div>
                      <div className="text-xl font-light mt-1 tabular-nums">{v}</div>
                    </div>
                  ))}
                </section>

                <section>
                  <div className="label-tag mb-2">// Observed Indicators</div>
                  <div className="panel-soft p-4 text-sm whitespace-pre-wrap leading-relaxed">{active.ai_observed_indicators}</div>
                </section>

                {active.admin_notes && (
                  <section>
                    <div className="label-tag mb-2">// Investigator Notes</div>
                    <div className="panel-soft p-4 text-sm whitespace-pre-wrap leading-relaxed">{active.admin_notes}</div>
                  </section>
                )}

                {active.confirmed_findings && (
                  <section>
                    <div className="label-tag mb-2">// Confirmed Findings</div>
                    <div className="panel-soft p-4 text-sm whitespace-pre-wrap leading-relaxed border-l-2 border-l-[color:var(--status-critical)]">{active.confirmed_findings}</div>
                  </section>
                )}

                <section>
                  <div className="label-tag mb-2">// Recommendations</div>
                  <div className="panel-soft p-4 text-sm whitespace-pre-wrap leading-relaxed">{active.ai_recommendations}</div>
                </section>

                {(active.ai_augmentations || []).length > 0 && (
                  <section data-testid="forensic-ai-augmentations" className="space-y-4">
                    {active.ai_augmentations.map((aug, i) => (
                      <div key={i}>
                        <div className="label-tag mb-2 text-[color:var(--brand)]">// {aug.section_title}</div>
                        <div className="panel-soft p-4 text-sm whitespace-pre-wrap leading-relaxed">{aug.body}</div>
                        <div className="font-mono text-[10px] uppercase tracking-widest text-[color:var(--text-muted)] mt-1">
                          Added by {aug.created_by} · {aug.created_at ? new Date(aug.created_at).toLocaleString() : ''}
                        </div>
                      </div>
                    ))}
                  </section>
                )}
              </>
            ) : (
              <div className="font-mono text-xs text-[color:var(--text-secondary)]">Select a report…</div>
            )}
          </article>
        </div>
      )}
    </div>
  );
};

export default ForensicReports;
